import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "intro",
    title: "1. What SolSpur is",
    body: (
      <>
        <p>
          SolSpur is a software-as-a-service platform that helps users design and publish marketing pages
          for their digital token projects. We sell three things:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>AI-generated branding assets (logo, whitepaper draft).</li>
          <li>Permanent hosting of your token's marketing page at <code className="font-mono text-[#cfff04]">solspur.com/c/[slug]</code>.</li>
          <li>Optional placement in our public directory at <code className="font-mono text-[#cfff04]">solspur.com/explore</code>.</li>
        </ul>
        <p className="mt-3">
          We <strong>do not</strong> custody, exchange, sell, or trade cryptocurrencies. We <strong>do not</strong> facilitate token
          sales or initial coin offerings. All blockchain transactions, where applicable, occur directly between
          users' self-custody wallets and are not routed through SolSpur.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    body: (
      <>
        <p>You must be at least 18 years old (or the age of majority in your jurisdiction) to use SolSpur.</p>
        <p className="mt-2">
          You may not use SolSpur if you reside in a jurisdiction sanctioned by OFAC, the EU, the UK, or the UN,
          or if you appear on any government denied-parties list.
        </p>
      </>
    ),
  },
  {
    id: "your-content",
    title: "3. Your content and tokens",
    body: (
      <>
        <p>When you publish a coin on SolSpur, you represent and warrant that:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>You own all rights to the content you submit (name, ticker, description, logo source, etc.).</li>
          <li>
            Your token does not constitute a security under the Howey Test or any applicable securities law in your
            jurisdiction. You have not promised, and will not promise, profits or returns to holders.
          </li>
          <li>You comply with MiCA (EU), FATF travel-rule guidance, OFAC sanctions, and applicable AML rules.</li>
          <li>Your content is not unlawful, fraudulent, infringing, defamatory, or designed to deceive purchasers.</li>
          <li>You hold SolSpur harmless from any claim arising out of your token, its sale, or its marketing.</li>
        </ul>
        <p className="mt-3">
          SolSpur reserves the right to remove any coin page that, in our sole discretion, violates these terms,
          targets vulnerable users, or impersonates real persons or trademarks.
        </p>
      </>
    ),
  },
  {
    id: "payments",
    title: "4. Payments and refunds",
    body: (
      <>
        <p>Pricing is one-time and shown on <Link className="text-[#cfff04] underline" to="/pricing">/pricing</Link>:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>$19</strong> — Publish a coin (permanent page + directory listing).</li>
          <li><strong>$29</strong> — Publish + Mint bundle (publish, free mint, 24h boost, waived mint fee).</li>
          <li><strong>$9</strong> — AI Launch Kit (tweet thread, pitch, meme captions + promo banner).</li>
          <li><strong>$9</strong> — Fresh Boost (24 hours at the top of /explore).</li>
          <li><strong>$49</strong> — Featured slot (7 days at top of /explore with gold badge).</li>
          <li><strong>$149</strong> — Homepage Hero (exclusive 30-day homepage placement).</li>
        </ul>
        <p className="mt-3">
          Payments are processed by Stripe, Inc. SolSpur does not store your card details. By paying, you also
          accept Stripe's own Terms of Service.
        </p>
        <p className="mt-3">
          <strong>Refund policy:</strong> Within 24 hours of publishing, if your coin has received zero public views,
          we'll refund in full on request. After 24 hours or any traffic, all sales are final — your coin's page is
          already part of public web history (Google cache, archive.org, social shares).
        </p>
        <p className="mt-3">
          Tips paid in SOL via Solana Pay go <strong>directly</strong> to the creator's wallet. SolSpur does not
          custody, route, or take a fee on tips.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "5. AI-generated content",
    body: (
      <>
        <p>
          Logos and whitepaper text are generated by third-party AI models (Google Gemini "Nano Banana" for images,
          Anthropic Claude for text) through the Emergent universal-key gateway. AI output:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Is provided as-is, without warranty of accuracy, originality, or fitness for purpose.</li>
          <li>May occasionally resemble existing logos or text — you are responsible for trademark / copyright clearance before commercial use.</li>
          <li>You own commercial rights to the generated outputs for your coin page, subject to OpenAI / Anthropic / Google upstream model terms.</li>
        </ul>
      </>
    ),
  },
  {
    id: "wallet",
    title: "6. Wallet connections",
    body: (
      <>
        <p>
          SolSpur supports optional wallet connections (Phantom for Solana) via the wallet
          extensions' own injected providers. We:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Never see or transmit your private keys.</li>
          <li>Never sign transactions on your behalf — you confirm every transaction in your wallet.</li>
          <li>Only read your public address and (optionally) SOL balance to display in the UI.</li>
        </ul>
        <p className="mt-3">
          If you link a wallet as the creator wallet for tip routing, that wallet address is stored publicly on
          your coin page so visitors can tip you. You may not be able to change a linked wallet after the fact.
        </p>
      </>
    ),
  },
  {
    id: "no-investment",
    title: "7. Not investment advice",
    body: (
      <p>
        Nothing on SolSpur constitutes investment, financial, tax, or legal advice. Cryptocurrencies are
        highly volatile and you can lose everything you put in. Talk to a qualified professional in your
        jurisdiction before launching a token, listing it on an exchange, or investing in one.
      </p>
    ),
  },
  {
    id: "ip",
    title: "8. Our intellectual property",
    body: (
      <p>
        The "SolSpur" name, logo, and the software powering this platform are owned by SolSpur Labs and its
        contributors. You may not scrape, clone, or republish the platform itself without written permission.
        You retain all rights to the coin content you submit, subject to Section 3 above.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "9. Disclaimer of warranties",
    body: (
      <p>
        SOLSPUR IS PROVIDED "AS IS." TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
        EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        OUR TOTAL LIABILITY FOR ANY CLAIM IS CAPPED AT THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS,
        OR USD $100, WHICHEVER IS LOWER.
      </p>
    ),
  },
  {
    id: "termination",
    title: "10. Termination",
    body: (
      <p>
        We may suspend or remove your coin pages at any time for violation of these terms, fraud, abuse, or
        as required by law. You may delete a draft coin yourself; to delete a published coin, email{" "}
        <a className="text-[#cfff04] underline" href="mailto:support@solspur.com">support@solspur.com</a> from the
        email you used at signup.
      </p>
    ),
  },
  {
    id: "law",
    title: "11. Governing law",
    body: (
      <p>
        These terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-laws
        rules. Disputes will be resolved in the state or federal courts located in Wilmington, Delaware.
      </p>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to these terms",
    body: (
      <p>
        We may update these terms from time to time. The "last updated" date below reflects the current version.
        Material changes will be flagged on the homepage for 7 days before taking effect. Continued use after
        that date constitutes acceptance.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="terms-page">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
            // Legal
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-2">
            Terms of Service
          </h1>
          <p className="text-[#e2e2e2] mb-10">Last updated: 27 May 2026</p>

          <div className="space-y-10 text-sm leading-relaxed text-[#efefef]">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} data-testid={`tos-${s.id}`}>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-3">
                  {s.title}
                </h2>
                <div className="space-y-2">{s.body}</div>
              </section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-[#3a3a3a] flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/privacy"
              className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04]"
              data-testid="tos-to-privacy"
            >
              ← Read Privacy Policy
            </Link>
            <a
              href="mailto:support@solspur.com"
              className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04]"
            >
              support@solspur.com
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
